import type { OnInit } from '@angular/core';
import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { PhoneConsentsModalComponent } from '@innogy/become-a-customer/shared';
import type { Offer } from '@innogy/become-a-customer/shared/interfaces';
import {
  energyAdvicePhoneRejectedFormControlID,
  sustainabilityAdvicePhoneRejectedFormControlID,
  type OrderConfirmationFormValues,
} from '@innogy/become-a-customer/state';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { getFieldValue, type Field } from '@sitecore-jss/sitecore-jss-angular';
import { SetValueAction } from 'ngrx-forms';

import { OrderFulfillmentStepComponent } from '../order-fulfillment-step.component';

@Component({
  selector: 'wl-order-fulfillment-confirmation[progressiveFormState]',
  templateUrl: './order-fulfillment-confirmation.component.html',
  styleUrls: [
    './order-fulfillment-confirmation.component.ed.scss',
    './order-fulfillment-confirmation.component.essent.scss',
  ],
})
export class OrderFulfillmentConfirmationComponent
  extends OrderFulfillmentStepComponent<OrderConfirmationFormValues>
  implements OnInit
{
  private readonly modalService = inject(NgbModal);
  private readonly store$ = inject(Store);

  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() offer?: Offer | null;
  @Output() openAOMModal = new EventEmitter<Offer>();

  ngOnInit(): void {
    this.progressiveFormCardContent = {
      title: this.fields.CheckCardTitle?.value,
      subtitle: this.fields.CheckCardSubtitle?.value,
      submitButtonText: this.fields.CheckCardSubmitButton?.value,
      stepNumber: this.stepNumber,
    };
  }

  get disclaimers(): DisclaimerLink[] {
    const disclaimers: DisclaimerLink[] = [
      {
        key: this.fields?.GeneralTermsAndConditions,
        href: this.offer?.generalTermsAndConditionsUrl ?? '',
        automationId:
          'order-fulfillment-check-condidions-general-terms-and-conditions',
      },
      {
        key: this.fields?.QualityCriteria,
        href: this.offer?.qualityCriteriaUrl ?? '',
        automationId: 'order-fulfillment-check-conditions-quality-criteria',
      },
      {
        key: this.fields?.ProductConditions,
        href: this.offer?.productConditionsUrl ?? '',
        automationId: 'order-fulfillment-check-conditions-product',
      },
    ];
    if (this.offer?.actionConditionsUrl) {
      disclaimers.push({
        key: this.fields?.CampaignConditions,
        href: this.offer?.actionConditionsUrl ?? '',
        automationId: 'order-fulfillment-check-conditions-campaign',
      });
    }

    return disclaimers;
  }

  openAOMModalClick(e: Event) {
    if (!this.offer) {
      throw new Error('No offer found to open AOM Modal with.');
    }
    e.preventDefault();
    this.openAOMModal.emit(this.offer);
  }

  async openConsentsModal() {
    const modal = this.modalService.open(PhoneConsentsModalComponent, {
      centered: true,
    });
    modal.componentInstance.rendering.set(this.rendering);
    modal.componentInstance.energyAdvicePhoneRejected.set(
      this.progressiveFormState?.formState.controls.energyAdvicePhoneRejected
        ?.value ?? false
    );
    modal.componentInstance.sustainabilityAdvicePhoneRejected.set(
      this.progressiveFormState?.formState.controls
        .sustainabilityAdvicePhoneRejected?.value ?? false
    );
    const modalResult = await modal.result;

    if (modalResult && 'energyAdvicePhoneRejected' in modalResult) {
      this.store$.dispatch(
        new SetValueAction(
          energyAdvicePhoneRejectedFormControlID,
          modalResult.energyAdvicePhoneRejected
        )
      );
    }
    if (modalResult && 'sustainabilityAdvicePhoneRejected' in modalResult) {
      this.store$.dispatch(
        new SetValueAction(
          sustainabilityAdvicePhoneRejectedFormControlID,
          modalResult.sustainabilityAdvicePhoneRejected
        )
      );
    }
  }

  get phoneConsentValues() {
    return {
      energyAdvice:
        this.progressiveFormState?.formState?.controls
          ?.energyAdvicePhoneRejected?.value,
      sustainability:
        this.progressiveFormState?.formState?.controls
          ?.sustainabilityAdvicePhoneRejected?.value,
    };
  }

  /**
   * Conditional dynamic rendering of the notification message
   * Preferable we don't build HTML manually but the info-card component does not support slots
   */
  get phoneConsentMessage() {
    let message = getFieldValue(this.rendering, 'ConsentsMessageBody', '');
    message += '<br>';
    if (
      this.phoneConsentValues.energyAdvice ||
      this.phoneConsentValues.sustainability
    ) {
      message += '<ul>';
      if (this.phoneConsentValues.energyAdvice) {
        message += `<li>${getFieldValue(this.rendering, 'EmailConsentsModalEnergyAdviceCheckbox', '')}</li>`;
      }
      if (this.phoneConsentValues.sustainability) {
        message += `<li>${getFieldValue(this.rendering, 'EmailConsentsModalSustainabilityCheckbox', '')}</li>`;
      }
      message += '</ul>';
    }
    return message;
  }
}

interface DisclaimerLink {
  key: Field;
  href: string;
  automationId: string;
}
